import Analytics from '@opencare/analytics';
import { IconInfoCircle } from '@tabler/icons-react';

import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { PrimaryButton } from 'brand-guidelines/Buttons-styled-components';
import { DatePickerInput } from 'common/components/DatePickerInput/DatePickerInput';
import { FieldValidation, useFormValidator } from 'common/Validator/Validator';
import { UpdatePatientDetails } from 'connect/saga/handlers/patient/update-details';
import { InputType } from 'pages/checkout/components/common/Input';
import { FormError } from 'pages/checkout/components/common/input-styled-components';
import { LabeledInput } from 'pages/checkout/components/common/LabeledInput/LabeledInput';
import { StepFormWrapper, StepHeader, StepHeaderSubtext, StepHeaderText } from 'pages/checkout/components/common/layout-styled-components';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { CheckoutState, DateOfBirth, SexAtBirth } from 'pages/checkout/connect/types';
import { useNextCheckoutStep } from 'pages/checkout/hooks/checkoutStepHooks';
import { formatBirthDate } from 'pages/checkout/utils/timeUtils';

import { useAnalyticsService } from 'services/AnalyticsService';
import { BooleanChoice, booleanChoiceToBoolean } from 'types/BooleanChoice';
import { useCheckoutPatient } from 'utils/customHooks/use-checkout-patient';
import { useIsPartnerUser } from 'utils/customHooks/use-partner-user';
import { useEffectOnce } from 'utils/customHooks/useEffectOnce';
import { usePatch } from 'utils/customHooks/usePatch';

import { googlePlaceActionLink } from 'utils/google';

import { Alert } from './Alert';
import { ALERT_THEMES } from './Alert-styled-components';
import { PATIENT_INFO_VALIDATOR_CONFIG } from './form-validator-configs';
import { FormRow, InsuranceStepWrapper } from './Insurance-styled-components';
import { FormInputConfig, FormInputs } from './renderer-helpers';

const analytics = new Analytics();
const sexOptions = [SexAtBirth.Female, SexAtBirth.Male];

// Process sex assigned at birth into API model attribute
const sexAssignedAtBirthToGender = (sexAssignedAtBirth: SexAtBirth) => {
  const sexToGenderMap = {
    [SexAtBirth.Female]: 'F',
    [SexAtBirth.Male]: 'M',
  };

  return sexToGenderMap[sexAssignedAtBirth] ?? 'O';
};

const genderToSexAssignedAtBirth = (gender: string): SexAtBirth | null => {
  const genderToSexAssignedAtBirthMap = {
    F: SexAtBirth.Female,
    M: SexAtBirth.Male,
  } as Record<string, SexAtBirth>;

  return genderToSexAssignedAtBirthMap[gender] ?? null;
};

interface ValidationState {
  allValid?: boolean;
  firstName?: FieldValidation;
  lastName?: FieldValidation;
  dateOfBirth?: FieldValidation;
  sexAssignedAtBirth?: FieldValidation;
  isReturningPatient?: FieldValidation;
}

export const PatientInfo = () => {
  const patchCheckout = usePatch<CheckoutState>({
    stateHandler: CheckoutStateHandler,
  });
  const analyticsService = useAnalyticsService();
  const patientFormValidator = useFormValidator(PATIENT_INFO_VALIDATOR_CONFIG);

  const [submitting, setSubmitting] = useState(false);

  const { patient: patientDataRedux } = useSelector<any, CheckoutState>(CheckoutStateHandler.selector);
  const isLoading = useSelector(UpdatePatientDetails.loading!.selector);
  const error = useSelector(UpdatePatientDetails.error!.selector);
  const success = useSelector(UpdatePatientDetails.success!.selector);
  const { goToNextStep } = useNextCheckoutStep();

  const isPartnerUser = useIsPartnerUser();
  const patient = useCheckoutPatient();

  const storedFirstName = useMemo<string>(
    () => patient?.firstName || patientDataRedux?.firstName || '',
    [patient, patientDataRedux],
  );

  const storedLastName = useMemo<string>(
    () => patient?.lastName || patientDataRedux?.lastName || '',
    [patient, patientDataRedux],
  );

  const storedSexAssignedAtBirth = useMemo<SexAtBirth | null>(
    () => genderToSexAssignedAtBirth(patient?.gender) || patientDataRedux?.sexAssignedAtBirth || null,
    [patient, patientDataRedux],
  );

  const storedDateOfBirth = useMemo<DateOfBirth | null>(() => {
    if (patient?.dateOfBirth) {
      const dateOfBirthMoment = moment(patient.dateOfBirth);
      const year = dateOfBirthMoment.year().toString();
      // moment is 0-indexed for months, so we need to add 1
      const month = (dateOfBirthMoment.month() + 1).toString();
      const day = dateOfBirthMoment.date().toString();

      return { year, month: month.padStart(2, '0'), day: day.padStart(2, '0') };
    }

    if (patientDataRedux?.dateOfBirth && !isPartnerUser) {
      return patientDataRedux.dateOfBirth;
    }

    return null;
  }, [patient, patientDataRedux, isPartnerUser]);

  const [firstName, setFirstName] = useState(storedFirstName);
  const [lastName, setLastName] = useState(storedLastName);
  const [year, setYear] = useState(storedDateOfBirth?.year || null);
  const [month, setMonth] = useState(storedDateOfBirth?.month || null);
  const [day, setDay] = useState(storedDateOfBirth?.day || null);
  const [sexAssignedAtBirth, setSexAssignedAtBirth] = useState(storedSexAssignedAtBirth);
  const [isReturningPatient, setIsReturningPatient] = useState<BooleanChoice | null>(null);

  const showReturningPatientToggle = Boolean(
    googlePlaceActionLink.getConversionData().rwgToken,
  );

  const formState = useMemo(() => ({
    firstName,
    lastName,
    dateOfBirth: {
      month,
      day,
      year,
    },
    sexAssignedAtBirth,
    ...showReturningPatientToggle && {
      isReturningPatient,
    },
  }), [
    firstName,
    lastName,
    month,
    day,
    year,
    sexAssignedAtBirth,
    isReturningPatient,
    showReturningPatientToggle,
  ]);

  const [formValidity, setFormValidity] = useState<ValidationState>({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const handleValidate = useCallback(({
    stateOverride,
  }: {
    stateOverride?: Partial<Record<keyof ValidationState, any>>;
  } = {}) => {
    const validationStateToUse = {
      ...formState,
      ...stateOverride,
    };

    const validationResult = patientFormValidator.validate(validationStateToUse, { showReturningPatientToggle });
    setFormValidity(validationResult);
    return validationResult;
  }, [formState, showReturningPatientToggle, patientFormValidator]);

  useEffect(() => {
    if (!formState) {
      return;
    }

    handleValidate();
  }, [handleValidate, formState]);

  const handleSexAtBirthSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSexAssignedAtBirth = event.target.value as SexAtBirth;
    setSexAssignedAtBirth(newSexAssignedAtBirth);
  };

  const handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFirstName = event.target.value;
    setFirstName(newFirstName);
  };

  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLastName = event.target.value;
    setLastName(newLastName);
  };

  const handleReturningPatientToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newReturningPatient = event.target.value as BooleanChoice;
    setIsReturningPatient(newReturningPatient);
  };

  const dispatch = useDispatch();

  // Track page view on mount
  useEffectOnce(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-patient-info',
      name: 'checkout',
      section: 'patient-info',
      ...analyticsService.analyticsData,
    });
  });

  const handleNextStep = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isFormValid = handleValidate();
    if (!isFormValid.allValid) {
      setShowValidationErrors(true);
      return;
    }

    // This isn't necessary sine we checked this in the form validator, but TS doesn't know that
    if (!year || !month || !day || !sexAssignedAtBirth) {
      setShowValidationErrors(true);
      return;
    }

    const patientDetails = {
      firstName,
      lastName,
      sexAssignedAtBirth: sexAssignedAtBirth!,
      dateOfBirth: { year, month, day },
      ...showReturningPatientToggle && {
        isReturningPatient: booleanChoiceToBoolean(isReturningPatient!),
      },
    };

    const birthDateString = formatBirthDate({ year, month, day });

    analytics.trackForm({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-patient-info',
      name: 'checkout',
      section: 'patient-info',
      isReturningPatient: showReturningPatientToggle
        ? Boolean(isReturningPatient)
        : undefined,
    });

    patchCheckout({
      patient: patientDetails,
    });

    const patientDetailsForApi = {
      firstName,
      lastName,
      gender: sexAssignedAtBirthToGender(sexAssignedAtBirth!),
      dateOfBirth: birthDateString,
    };

    setSubmitting(true);
    // Update patient in API
    dispatch(
      UpdatePatientDetails.createAction({
        patientDetails: patientDetailsForApi,
        autoVerify: !isPartnerUser,
      }),
    );
  };

  // If form is invalid, show validation errors when clicking the button
  const handleDisabledButtonClick = () => {
    if (formValidity.allValid) {
      return;
    }

    setShowValidationErrors(true);
  };

  // Watch for success state changes
  useEffect(() => {
    if (!submitting || !success || isLoading) {
      return;
    }

    // Mark success as handled
    dispatch(UpdatePatientDetails.success!.update(null));
    goToNextStep();
  }, [success, isLoading, dispatch, goToNextStep, submitting]);

  const nameInputs: FormInputConfig[] = [
    {
      label: 'First Name',
      type: InputType.TEXT,
      autoComplete: 'given-name',
      required: true,
      value: firstName,
      onChange: handleFirstNameChange,
      errorMessage: showValidationErrors ? formValidity.firstName?.message : undefined,
    },
    {
      label: 'Last Name',
      type: InputType.TEXT,
      autoComplete: 'family-name',
      required: true,
      value: lastName,
      onChange: handleLastNameChange,
      errorMessage: showValidationErrors ? formValidity.lastName?.message : undefined,
    },
  ];

  const dateOfBirthErrorMessage = showValidationErrors
    ? formValidity.dateOfBirth?.message
    : undefined;

  const sexAtBirthInput: FormInputConfig[] = [
    {
      label: 'Sex assigned at birth',
      tooltip: 'This information is needed for insurance purposes. ' + (isPartnerUser
        ? "You can specify patient's gender details in the notes section on the next page."
        : "If you'd like, you can share your gender details in the notes section on the next page."),
      type: InputType.TOGGLE,
      required: true,
      options: sexOptions,
      value: sexAssignedAtBirth,
      onChange: handleSexAtBirthSelect,
      errorMessage: showValidationErrors ? formValidity.sexAssignedAtBirth?.message : undefined,
    },
  ];

  const returningPatientToggle: FormInputConfig[] = [
    {
      label: 'Have you visited this practice before?',
      type: InputType.TOGGLE,
      required: showReturningPatientToggle,
      value: isReturningPatient,
      onChange: handleReturningPatientToggleChange,
      options: [BooleanChoice.Yes, BooleanChoice.No],
      errorMessage: showValidationErrors ? formValidity.isReturningPatient?.message : undefined,
    },
  ];

  return (
    <InsuranceStepWrapper onSubmit={handleNextStep}>
      <StepHeader>
        <StepHeaderText>Patient Information</StepHeaderText>
        <StepHeaderSubtext>Who is coming in to see the dentist?</StepHeaderSubtext>
      </StepHeader>

      <StepFormWrapper>
        <Alert theme={ALERT_THEMES.success} customIcon={<IconInfoCircle />}>
          {isPartnerUser
            ? "Enter patient's details as shown on their government issued ID"
            : 'Enter your details as shown on your government issued ID'}
        </Alert>

        {error && <FormError>{error}</FormError>}

        <FormRow>
          <FormInputs inputConfigs={nameInputs} />
        </FormRow>

        <LabeledInput
          label="Date of Birth"
          tooltip={isPartnerUser
            ? "Enter patient's date of birth as shown on their government issued ID"
            : 'Enter your date of birth as shown on your government issued ID'}
        >
          <DatePickerInput
            monthValue={month}
            dayValue={day}
            yearValue={year}
            onMonthChange={setMonth}
            onDayChange={setDay}
            onYearChange={setYear}
            errorMessage={dateOfBirthErrorMessage}
            required
            max={moment()}
          />
        </LabeledInput>

        <FormRow>
          <FormInputs inputConfigs={sexAtBirthInput} />
        </FormRow>

        {showReturningPatientToggle
          && (
            <FormRow>
              <FormInputs inputConfigs={returningPatientToggle} />
            </FormRow>
          )}

      </StepFormWrapper>
      <PrimaryButton
        type="submit"
        onClick={handleDisabledButtonClick}
        $loading={isLoading}
        $disabled={!formValidity.allValid}
      >
        Next Step
      </PrimaryButton>
    </InsuranceStepWrapper>
  );
};
